import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Container,
  useMediaQuery,
  Button,
  TextField,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import mapboxgl from "mapbox-gl";

import imageCompression from "browser-image-compression";
import { Input } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { styled, useTheme } from "@mui/material/styles";
import {
  AllTypoFontStyles,
  addEventMainStyles,
} from "../../../../Styles/styles";
import { useCreateEventMutation } from "../../../../Redux/apis/ApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { initializeMapbox } from "../../../../utils/mapboxUtils";

dayjs.extend(customParseFormat);

export default function BasicInfo({
  onNext,
  eventData,
  setEventData,
  setLoading,
  handleBlur,
  errors,
}) {
  const [location, setLocation] = useState("");
  const editEventIfo = useSelector((state) => state.editEventInfo.editEvent);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (editEventIfo && editEventIfo.event) {
      const { event } = editEventIfo;
      setEventData({
        name: event.eventName || "",
        description: event.description || "",
        location: event.location || "",
        organizationName: event.organizationName || "",
        date: event.date
          ? dayjs(event.date).format("MM/DD/YYYY")
          : dayjs().format("MM/DD/YYYY"),
        time: event.time ? event.time : dayjs().format("hh:mm A"),
        endTime: event.endTime
          ? event.endTime
          : dayjs().add(1, "hour").format("hh:mm A"),
        latitude: event.latitude || "",
        longitude: event.longitude || "",
        startDateTime: event.startDateTime || "",
        endDateTime: event.endDateTime || "",
        eventPhotoUrls: event.eventPhoto || [],
      });
      // setImagePreviews(event.eventPhoto ? [event.eventPhoto] : []);
      setImagePreviews(event.eventPhoto || []);
    } else {
      setEventData({
        name: "",
        description: "",
        location: "",
        organizationName: "",
        date: dayjs().format("MM/DD/YYYY"),
        time: dayjs().format("hh:mm A"),
        endTime: dayjs().add(1, "hour").format("hh:mm A"),
        latitude: "",
        longitude: "",
        startDateTime: dayjs(),
        endDateTime: dayjs().add(1, "hour"),
        eventPhotoUrls: [],
      });
      setImagePreviews([]);
    }
  }, [editEventIfo]);

  const inputStyle = {
    // height: "2rem",
    // marginBottom: "0.5rem",
    alignSelf: "center",
    padding: "0.4rem 0.4rem",
    fontSize: "14px",
    border: "1px solid #ccc",
    borderRadius: "9px",
    color: "#202227",
  };
  const [loader, setLoader] = useState(false);

  const mapContainerRef = useRef(null);
  const geocoderContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderInstanceRef = useRef(null);
  const locationPlaceholder = eventData?.location
    ? eventData.location
    : editEventIfo?.event
    ? editEventIfo.event.location
    : "Search for location...";

  useEffect(() => {
    if (!mapInstanceRef.current) {
      const { map, geocoder } = initializeMapbox(
        mapContainerRef,
        geocoderContainerRef,
        (place_name, lat, lng) => {
          setEventData((prevData) => ({
            ...prevData,
            location: place_name,
            latitude: lat,
            longitude: lng,
          }));
        },
        locationPlaceholder
      );

      mapInstanceRef.current = map;
      geocoderInstanceRef.current = geocoder;
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      if (geocoderInstanceRef.current) {
        geocoderInstanceRef.current = null;
      }
    };
  }, []);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [images, setImages] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEventData({ ...eventData, [name]: value });
  };

  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setLoading(true);

      const newImages = [];
      const newImagePreviews = [];

      try {
        for (const file of files) {
          const previewUrl = URL.createObjectURL(file);
          newImagePreviews.push(previewUrl);
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
          };
          const compressedFile = await imageCompression(file, options);
          const sanitizedFileName = file.name
            .replace(/[^a-zA-Z0-9._-]/g, "")
            .replace(/\s+/g, "_");
          const newFile = new File([compressedFile], sanitizedFileName, {
            type: "image/jpeg",
          });
          newImages.push(newFile);
        }
        setImagePreviews(newImagePreviews || []);
        setImages(newImages);

        // Upload each image to S3 and get URLs
        const imageUrls = await Promise.all(newImages.map(uploadFileToS3));
        setEventData((prevData) => ({
          ...prevData,
          eventPhotoUrls: imageUrls,
        }));
      } catch (error) {
        console.error("Error processing files:", error);
      } finally {
        setLoading(false); // Stop loading
      }
    }
  };

  const InputPic = styled("input")({
    display: "none",
  });

  const [timeError, setTimeError] = useState(""); // To store time error

  const onStartDateTimeChange = (newValue) => {
    setEventData((prev) => ({
      ...prev,
      startDateTime: newValue,
    }));
  };

  const onEndDateTimeChange = (newValue) => {
    setEventData((prev) => ({
      ...prev,
      endDateTime: newValue,
    }));
  };

  const apiUrl = process.env.REACT_APP_API_URL;

  //s3
  const uploadFileToS3 = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${apiUrl}/upload`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const result = await response.json();
      return result.Location;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  useEffect(() => {
    const { map, geocoder } = initializeMapbox(
      mapContainerRef,
      geocoderContainerRef,
      (place_name, lat, lng) => {
        setEventData((prevData) => ({
          ...prevData,
          location: place_name,
          latitude: lat,
          longitude: lng,
        }));
      }
    );

    mapInstanceRef.current = map;
    geocoderInstanceRef.current = geocoder; // Store geocoder instance

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      // No need to call `off` on Geocoder
      if (geocoderInstanceRef.current) {
        // Simply remove from DOM or reset if needed
        geocoderInstanceRef.current = null;
      }
    };
  }, []);

  const getCurrentLocation = async () => {
    setLoader(true);
    setError("");

    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      setLoader(false);
      return;
    }

    setLoader(true); // Show loader

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Reverse geocode to get the location name
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${mapboxgl.accessToken}`
        );
        const data = await response.json();
        const placeName = data.features[0]?.place_name || "Unknown location";
        setLocation(placeName);
        const { geocoder } = initializeMapbox(
          mapContainerRef,
          geocoderContainerRef,
          () => {}
        );

        if (geocoder) {
          geocoder.setInput(placeName); // Correct method to set location in geocoder input
        }

        setEventData((prevData) => ({
          ...prevData,
          location: placeName,
          latitude: latitude,
          longitude: longitude,
        }));

        if (geocoderContainerRef.current) {
          const inputField = geocoderContainerRef.current.querySelector(
            ".mapboxgl-ctrl-geocoder--input"
          );
          if (inputField) {
            inputField.value = placeName;
          }
        }

        setLoader(false); // Hide loader
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setError(
            "Location access denied. Please enable it in your browser settings."
          );
        } else {
          setError("Unable to retrieve location. Please try again.");
        }
        setLoader(false);
      }
    );
  };

  useEffect(() => {
    const { map, geocoder } = initializeMapbox(
      mapContainerRef,
      geocoderContainerRef,
      (place_name, lat, lng) => {
        setEventData((prevData) => ({
          ...prevData,
          location: place_name,
          latitude: lat,
          longitude: lng,
        }));
        setLocation(place_name);
      }
    );

    mapInstanceRef.current = map;
    geocoderInstanceRef.current = geocoder;

    return () => {
      if (mapInstanceRef.current) {
        if (mapInstanceRef.current instanceof mapboxgl.Map) {
          mapInstanceRef.current.remove();
        }
        mapInstanceRef.current = null;
      }
      if (geocoderInstanceRef.current) {
        geocoderInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <Grid container spacing={2} p={{ md: 10, xs: 2 }}>
      <Grid item xs={12} md={6} lg={5}>
        <Box mb={2}>
          <Typography sx={AllTypoFontStyles.CommonTypo2}>
            Event Name *
          </Typography>
          <Input
            required
            name="name"
            size="large"
            placeholder="Event Name"
            value={eventData.name}
            onChange={handleChange}
            fullWidth
          />
        </Box>

        <Box mb={2}>
          <Typography sx={AllTypoFontStyles.CommonTypo2}>
            Description *
          </Typography>
          <TextField
            required
            name="description"
            size="large"
            placeholder="Description"
            value={eventData.description}
            onChange={handleChange}
            fullWidth
            multiline
            InputProps={{
              sx: { resize: "vertical" },
            }}
            rows={3}
          />
        </Box>

        <Box mb={1} sx={{ width: "100%" }}>
          <Typography sx={AllTypoFontStyles.CommonTypo2}>Location</Typography>
          <Box
            value={eventData.location || ""}
            ref={geocoderContainerRef}
            sx={{
              width: "100%",
              marginBottom: "1px",
              "& .mapboxgl-ctrl-geocoder": {
                width: "100%",
                border: "none",
                boxShadow: "none",
                maxWidth: "none",
                "& :focus": {
                  outline: "none",
                },
              },
              "& .mapboxgl-ctrl-geocoder--input": {
                height: "auto",
              },
              "& .mapboxgl-ctrl-geocoder--icon": {
                top: 6,
              },
              "& .mapboxgl-ctrl-geocoder--input::placeholder": {
                color: "#B8B8B8",
                fontSize: "1rem",
                fontWeight: "500",
              },
              ...inputStyle,
            }}
          ></Box>
          <Button
            sx={{ textAlign: "left", fontSize: "12px" }}
            onClick={getCurrentLocation}
            disabled={loader}
          >
            {loader ? "Getting location..." : "Click to set current location"}
          </Button>
          {error && (
            <Typography color="error" sx={{ fontSize: "12px" }}>
              {error}
            </Typography>
          )}
          <Box
            ref={mapContainerRef}
            style={{
              height: "150px",
              marginTop: "8px",
              display: "none",
            }}
          />
        </Box>

        <Box mb={2}>
          <Typography sx={AllTypoFontStyles.CommonTypo2}>
            Organization Name
          </Typography>
          <Input
            name="organizationName"
            size="large"
            placeholder="Organization Name"
            value={eventData.organizationName}
            onChange={handleChange}
            fullWidth
          />
        </Box>
        <Grid item xs={12}>
          <Box mb={2} sx={{ width: "100%", "& > *": { width: "100%" } }}>
            <Typography sx={AllTypoFontStyles.CommonTypo2}>
              Start Date & Time * ({timeZone})
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                timeSteps={{ minutes: 1 }}
                minutesStep={1}
                size="small"
                format="MM/DD/YYYY hh:mm A" // Adjust format as needed
                value={
                  eventData?.startDateTime
                    ? dayjs(eventData.startDateTime)
                    : null
                }
                onChange={onStartDateTimeChange}
                disablePast
                slotProps={{ textField: { fullWidth: true } }}
                placeholder="Select Start Date & Time"
                shouldDisableDate={(date) =>
                  date.isBefore(dayjs().startOf("day"))
                }
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box mb={2} sx={{ width: "100%", "& > *": { width: "100%" } }}>
            <Typography sx={AllTypoFontStyles.CommonTypo2}>
              End Date & Time *
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                timeSteps={{ minutes: 1 }} // Ensures 1-minute increments
                size="small"
                format="MM/DD/YYYY hh:mm A" // Adjust format as needed
                value={
                  eventData?.endDateTime ? dayjs(eventData.endDateTime) : null
                }
                onChange={onEndDateTimeChange}
                disablePast
                slotProps={{ textField: { fullWidth: true } }}
                placeholder="Select End Date & Time"
                shouldDisableDate={(date) =>
                  eventData.startDateTime
                    ? date.isBefore(
                        dayjs(eventData.startDateTime).startOf("day")
                      )
                    : date.isBefore(dayjs().startOf("day"))
                }
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        {timeError && (
          <Typography color="error" sx={{ mt: 1 }}>
            {timeError}
          </Typography>
        )}
      </Grid>

      {/* Right Column - Upload Event Photos */}
      <Grid item xs={12} md={6} lg={7} mt={0}>
        <Box>
          <Container sx={addEventMainStyles.UploadPicContainer}>
            <Typography sx={AllTypoFontStyles.CommonTypo2}>
              Upload Event Photos
            </Typography>
          </Container>

          <Container sx={addEventMainStyles.UploadPicContainer}>
            <InputPic
              accept="image/*"
              id="event-photo"
              type="file"
              multiple
              onChange={handlePhotoChange}
            />
            <Container sx={{ justifyContent: "center", textAlign: "center" }}>
              <Grid container sx={addEventMainStyles.UploadPicGrid}>
                <label htmlFor="event-photo">
                  <Container sx={addEventMainStyles.UploadPicIconContainer}>
                    <IconButton
                      color="primary"
                      aria-label="upload picture"
                      component="span"
                    >
                      {imagePreviews.length > 0 ? (
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            height: "200px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={
                              editEventIfo?.event?.eventPhoto[0] ||
                              imagePreviews[0]
                            }
                            alt="Selected"
                            style={{
                              width: "200px",
                              height: "85%",
                              objectFit: "contain",
                            }}
                          />

                          {imagePreviews.length > 1 && (
                            <Typography
                              sx={{
                                position: "absolute",
                                bottom: "10px",
                                right: "10px",
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                color: "white",
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              +{imagePreviews.length - 1}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <AddPhotoAlternateIcon
                          sx={addEventMainStyles.UploadPhotoIcn}
                        />
                      )}
                    </IconButton>
                  </Container>
                  <Typography sx={addEventMainStyles.UploadPicSilverTypo}>
                    Upload Event Photos
                  </Typography>
                </label>
              </Grid>
            </Container>
          </Container>
        </Box>
      </Grid>
    </Grid>
  );
}
